import { useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AnimalTypeFilterProps {
  selected: string[];
  onChange: (selected: string[]) => void;
}

// Animal types with emoji icons
export const ANIMAL_TYPES = [
  { id: 'lion', name: 'Lions', emoji: '🦁' },
  { id: 'elephant', name: 'Elephants', emoji: '🐘' },
  { id: 'giraffe', name: 'Giraffes', emoji: '🦒' },
  { id: 'tiger', name: 'Tigers', emoji: '🐯' },
  { id: 'monkey', name: 'Monkeys', emoji: '🐒' },
  { id: 'panda', name: 'Pandas', emoji: '🐼' },
  { id: 'penguin', name: 'Penguins', emoji: '🐧' },
  { id: 'koala', name: 'Koalas', emoji: '🐨' },
  { id: 'bear', name: 'Bears', emoji: '🐻' },
  { id: 'dolphin', name: 'Dolphins', emoji: '🐬' },
  { id: 'whale', name: 'Whales', emoji: '🐋' },
  { id: 'crocodile', name: 'Crocodiles', emoji: '🐊' },
  { id: 'snake', name: 'Snakes', emoji: '🐍' },
  { id: 'rhino', name: 'Rhinos', emoji: '🦏' },
  { id: 'zebra', name: 'Zebras', emoji: '🦓' },
  { id: 'hippo', name: 'Hippos', emoji: '🦛' },
  { id: 'kangaroo', name: 'Kangaroos', emoji: '🦘' },
  { id: 'camel', name: 'Camels', emoji: '🐪' },
  { id: 'bird', name: 'Birds', emoji: '🦜' },
  { id: 'sea-life', name: 'Sea Life', emoji: '🐠' },
];

export function AnimalTypeFilter({ selected, onChange }: AnimalTypeFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const toggleAnimalType = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(type => type !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const filteredAnimalTypes = searchQuery.trim() === ''
    ? ANIMAL_TYPES
    : ANIMAL_TYPES.filter(animal => 
        animal.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            placeholder="Search animal types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <Search className="h-4 w-4" />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Selected Animals Pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map(id => {
            const animal = ANIMAL_TYPES.find(a => a.id === id);
            if (!animal) return null;
            
            return (
              <div 
                key={id}
                className="inline-flex items-center bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
              >
                <span className="mr-1">{animal.emoji}</span>
                <span>{animal.name}</span>
                <button 
                  className="ml-1 text-primary hover:text-[#1F4220]"
                  onClick={() => toggleAnimalType(id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          {selected.length > 1 && (
            <button 
              onClick={() => onChange([])}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Animal Types Grid */}
      <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar-dark">
        {filteredAnimalTypes.map((animal) => {
          const isSelected = selected.includes(animal.id);
          return (
            <button
              key={animal.id}
              onClick={() => toggleAnimalType(animal.id)}
              className={cn(
                "flex items-center p-2 rounded-lg transition-colors border",
                isSelected 
                  ? "bg-primary/10 border-primary/30 text-primary"
                  : "bg-white border-gray-200 hover:bg-gray-50 text-gray-700"
              )}
            >
              <div className="w-8 h-8 flex items-center justify-center mr-2 text-xl">
                {animal.emoji}
              </div>
              <span className="flex-1 text-sm text-left">{animal.name}</span>
              {isSelected && (
                <Check className="h-4 w-4 ml-2" />
              )}
            </button>
          );
        })}
      </div>

      {filteredAnimalTypes.length === 0 && (
        <p className="text-gray-500 text-sm py-3 text-center">
          No animal types found matching "{searchQuery}"
        </p>
      )}

      {/* Selected Count */}
      {selected.length > 0 && (
        <div className="pt-2 text-xs text-gray-500">
          {selected.length} animal type{selected.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
} 